import { Box, Center, Loader, MantineSize, Table, TableProps, Text } from '@mantine/core'
import { CSSProperties } from 'react'
import { useTranslate } from 'react-admin'
import { LXTableDataType } from '../../types/components/Table.type'
import { LXTd } from './LXTd'
import { LXTh } from './LXTh'

import styles from './LXTable.module.scss'

export type LXTableColumn<T = DefaultItem, ST = unknown> = {
  key: keyof T
  value: string
  sortable?: boolean
  type?: LXTableDataType
  // sortByField?: keyof ST
  size?: MantineSize
  maxHeight?: number
  // defaultSortDir?: LXSortDir
  headerPosition?: CSSProperties['justifyContent']
}

type DefaultItem = {
  id: string
} & Record<string, unknown>

type LXTableProps<T extends DefaultItem, ST = any> = TableProps & {
  items: T[]
  columns: LXTableColumn<T, ST>[]
  wrapperStyle?: CSSProperties
  loading?: boolean
  onRowClick?: (item: T) => void
  setActions?: (item: T) => JSX.Element[]
}

export const LXTable = <T extends object & DefaultItem>({
  items,
  columns,
  setActions,
  onRowClick,
  wrapperStyle,
  loading = false,
  ...tableProps
}: LXTableProps<T>) => {
  const translate = useTranslate()

  const rows = items.map((item, index) => (
    <Table.Tr
      key={`row-${index}`}
      onClick={() => onRowClick?.(item)}
      onKeyDown={() => onRowClick?.(item)}
      className={onRowClick ? styles.cursorPointer : ''}
    >
      {columns.map((c, kindex) => (
        <LXTd
          key={`cell-${index}-${kindex}`}
          type={c.type}
          data={(item as any)[c.key]}
          maxHeight={c.maxHeight}
        />
      ))}
      {setActions && <td key={`cell-${item.id}-actions`}>{setActions(item)}</td>}
    </Table.Tr>
  ))

  return loading ? (
    <Center w="100%" mb="xl">
      <Loader className="block my-0 mx-auto mt-2" />
    </Center>
  ) : (
    <Box className="mx-0 my-8 overflow-y-auto" style={wrapperStyle}>
      <Table
        horizontalSpacing="md"
        verticalSpacing="xs"
        highlightOnHover={!!onRowClick}
        // sx={{ tableLayout: 'fixed', minWidth: 700 }}
        {...tableProps}
      >
        <Table.Thead>
          <Table.Tr>
            {columns.map(c => (
              <LXTh key={c.key as string} justify={c.headerPosition} size={c.size}>
                {c.value}
              </LXTh>
            ))}
            {setActions && <th className="sticky top-0 bg-white z-10" />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.length > 0 ? (
            rows
          ) : (
            <Table.Tr>
              <Table.Td colSpan={columns.length}>
                <Text fw={500} ta="center">
                  {translate('_generics.noData')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Box>
  )
}
